import { doc, getDoc, onSnapshot } from "firebase/firestore";
import { useEffect, useRef } from "react";
import { useQuery, useQueryClient } from "react-query";
import { FirestoreDB } from "utils/firebase";

export const strategicGoalQueryKey = (goalDocId) => ["strategicGoal", goalDocId];

/**
 * Live view of a meeting's strategic goal.
 *
 * The post-meeting pipeline writes this doc minutes after the call ends, while
 * the user is often still sitting on the meeting detail — so it has to stream
 * rather than be fetched once.
 *
 * `goalDocId` is `recurringEventId || eventId`: every occurrence of a recurring
 * series shares one doc, so keying on it collapses them into a single cache
 * entry, and Firestore dedupes the listeners onto a single target.
 */
const useStrategicGoalSubscription = (goalDocId) => {
  const queryClient = useQueryClient();
  const hasSnapshotRef = useRef(false);

  const query = useQuery({
    queryKey: strategicGoalQueryKey(goalDocId),
    // Only seeds the first paint, so it does not have to wait on the listener
    // attaching. The two race, so the listener always wins: a getDoc that
    // resolves late would otherwise pin a stale value until the next write.
    queryFn: async () => {
      const snapshot = await getDoc(doc(FirestoreDB, "strategicGoals", goalDocId));
      if (hasSnapshotRef.current) {
        return queryClient.getQueryData(strategicGoalQueryKey(goalDocId)) ?? null;
      }
      return snapshot.data() ?? null;
    },
    enabled: Boolean(goalDocId),
    // The subscription below is the only thing that can change this value, so
    // remounts paint from cache instead of flashing a skeleton.
    staleTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (!goalDocId) return undefined;

    hasSnapshotRef.current = false;

    return onSnapshot(
      doc(FirestoreDB, "strategicGoals", goalDocId),
      (snapshot) => {
        hasSnapshotRef.current = true;
        queryClient.setQueryData(
          strategicGoalQueryKey(goalDocId),
          snapshot.data() ?? null
        );
      },
      (error) => {
        console.error("Failed to subscribe to strategic goal updates", error);
      }
    );
  }, [goalDocId, queryClient]);

  return query;
};

export default useStrategicGoalSubscription;
